import {
  Box,
  createStyles,
  Divider,
  Group,
  Menu,
  MenuProps,
} from '@mantine/core';
import { filter } from 'lodash/fp';
import React, { useMemo, useRef, useState } from 'react';
import { useKey } from 'react-use';

import { useActionDispatch, useActiveHub } from '@portals/redux';
import { setActiveHub } from '@portals/redux/actions/ui';
import { PartnerConfigurationType } from '@portals/types';
import { useOnClickOutside } from '@portals/utils';

import { HubMenuItem } from './HubMenuItem';
import { HUBS_LIST } from './hubs-menu.constants';
import { useCommonConfig } from '../../../../../hooks/portal-config';
import { useHasSupportSeat } from '../../../../../hooks/support-seats';
import { usePermissionAccess } from '../../../../permission-access/use-permission-access';
import { useShouldDisplayTenantMenu } from '../hooks';
import { PartnerMenuContent } from '../TenantMenuContent';
import { PartnerUser } from '../TenantUser';

const [homeHub, ...hubs] = HUBS_LIST;

export function HubsMenu() {
  const { classes, cx, theme } = useStyles();
  const [isOpen, setIsOpen] = useState(false);

  const { canView, isAdmin, canEdit } = usePermissionAccess();
  const configuration = useCommonConfig();
  const hasSupportSeat = useHasSupportSeat();

  const activeHubType = useActiveHub();
  const setActiveHubType = useActionDispatch(setActiveHub);

  const contentRef = useRef<HTMLDivElement>(null);

  const onEscape = () => {
    if (isOpen) {
      setIsOpen(false);
    }
  };

  useOnClickOutside(contentRef, () => setIsOpen(false), isOpen);
  useKey('Escape', onEscape, { event: 'keydown' }, [isOpen]);

  const shouldDisplayTenantMenu = useShouldDisplayTenantMenu();

  const filteredHubs = useMemo(
    () =>
      filter((hub) => {
        if (!hub.canAccessRoute) return true;

        return hub.canAccessRoute({
          configuration: configuration?.data as PartnerConfigurationType,
          canView,
          canEdit,
          isAdmin,
          hasSupportSeat,
        });
      }, hubs),
    [configuration, canView, canEdit, isAdmin, hasSupportSeat]
  );

  if (!shouldDisplayTenantMenu) {
    return (
      <Group
        h={theme.other.navbarHeight}
        align="center"
        spacing={0}
        className={classes.container}
      >
        <Box h="100%" w={300} p="xs">
          <PartnerUser isActive={false} isDisabled isHubsEnabled />
        </Box>

        <Divider
          className={classes.divider}
          orientation="vertical"
          color="blue_gray.7"
          m={0}
        />

        <Group align="center" spacing="sm" h="100%" p={0} ml="md">
          <HubMenuItem
            activeHubType={activeHubType}
            setActiveHubType={setActiveHubType}
            icon={homeHub.icon}
            hubType={homeHub.hubType}
          />

          <Divider
            className={classes.divider}
            orientation="vertical"
            color="blue_gray.7"
          />

          {filteredHubs.map((hub) => (
            <HubMenuItem
              key={hub.hubType}
              activeHubType={activeHubType}
              setActiveHubType={setActiveHubType}
              label={hub.label}
              hubType={hub.hubType}
            />
          ))}
        </Group>
      </Group>
    );
  }

  return (
    <Group h={theme.other.navbarHeight} align="center" spacing={0}>
      <Menu
        offset={0}
        withinPortal
        opened={isOpen}
        closeOnItemClick={false}
        position="bottom"
        shadow="xl"
        width={300}
        styles={menuStyles}
      >
        <Menu.Target>
          <Box
            w={300}
            className={classes.menuTarget}
            onClick={() => setIsOpen((curr) => !curr)}
          >
            <Box className={cx(classes.menuTargetWrapper, { active: isOpen })}>
              <PartnerUser isActive={isOpen} isHubsEnabled />
            </Box>
          </Box>
        </Menu.Target>

        <Menu.Dropdown>
          <PartnerMenuContent
            ref={contentRef}
            onClose={() => setIsOpen(false)}
          />
        </Menu.Dropdown>
      </Menu>

      <Divider
        className={classes.divider}
        orientation="vertical"
        color="blue_gray.7"
        m={0}
      />

      <Group align="center" spacing="sm" h="100%" p={0} ml="md">
        <HubMenuItem
          activeHubType={activeHubType}
          setActiveHubType={setActiveHubType}
          icon={homeHub.icon}
          hubType={homeHub.hubType}
        />

        <Divider
          className={classes.divider}
          orientation="vertical"
          color="blue_gray.7"
        />

        {filteredHubs.map((hub) => (
          <HubMenuItem
            key={hub.hubType}
            activeHubType={activeHubType}
            setActiveHubType={setActiveHubType}
            label={hub.label}
            hubType={hub.hubType}
          />
        ))}
      </Group>
    </Group>
  );
}

const menuStyles: MenuProps['styles'] = {
  dropdown: {
    padding: `0 !important`,
    borderBottomLeftRadius: 0,
    borderTopLeftRadius: 0,
  },
};

const useStyles = createStyles((theme) => ({
  container: {
    '.tenant-user-container': {
      cursor: 'default',

      '.tenant-user-menu-name': {
        color: theme.white,
      },
    },
  },
  menuTarget: {
    height: theme.other.navbarHeight,
    width: '100%',
    overflow: 'hidden',
    padding: theme.spacing.xs,
  },
  menuTargetWrapper: {
    paddingInline: theme.spacing.xs,
    borderRadius: theme.radius.sm,

    '&:hover, &.active': {
      backgroundColor: '#3C3E5E',
    },

    '.tenant-user-menu-name': {
      color: theme.white,
    },
  },
  divider: {
    alignSelf: 'unset',
    height: theme.spacing.xxl,
  },
}));
